import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Box, Button, Paper } from '@mui/material';
import { GridColDef, GridRowsProp } from '@mui/x-data-grid';

import { User } from '../user';
import { CustomGridColDef, DataGridCustom } from '../../../common/Datagrid';
import { getUsers } from '../routes/getUsers';
import { BaseModal } from '../../../common/Modals';
import UserForm from './UserForm';

export interface DataGridProps {
  rows: [];
  columns: GridColDef[];
  pageSize: number;
  checkboxSelection: boolean;
  title: string;
  action: () => void;
  actionTitle: string;
  onRowClick?: (row: any) => void;
  height?: number;
}

const columns: CustomGridColDef[] = [
  {
    field: 'firstName',
    headerName: 'Nome',
    flex: 1,
    editable: false,
    importance: 0
  },
  {
    field: 'lastName',
    headerName: 'Cognome',
    flex: 1,
    editable: false,
    importance: 0
  },
  {
    field: 'role',
    headerName: 'Ruolo',
    flex: 1,
    editable: false,
    importance: 0,
    valueGetter: (value, row) => {
      const roleMapping: { [key: number]: string } = {
        1: 'Admin',
        2: 'User',
        3: 'Guest'
      };
      return roleMapping[value] || 'Unknown';
    }
  },

  {
    field: 'creationDate',
    headerName: 'Data creazione',
    type: 'date',
    flex: 1,
    editable: false,
    align: 'right',
    importance: 1280,
    valueGetter: (value, row) => {
      // ISO String to Date conversion
      return row.creationDate ? new Date(row.creationDate.replace('Z', '')) : null;
    },
    valueFormatter: (value) => {
      if (!value) return '';
      return format(new Date(value), 'dd/MM/yyyy HH:mm:ss');
    }
  },
  {
    field: 'lastLogin',
    headerName: 'Ultimo accesso',
    type: 'date',
    flex: 1,
    editable: false,
    align: 'right',
    importance: 1024,
    valueGetter: (value, row) => {
      // ISO String to Date conversion
      return row.lastLogin ? new Date(row.lastLogin.replace('Z', '')) : null;
    },
    valueFormatter: (value) => {
      if (!value) return '';
      return format(new Date(value), 'dd/MM/yyyy HH:mm:ss');
    }
  },
  {
    field: 'email',
    headerName: 'Email',
    type: 'string',
    flex: 1,
    editable: false,
    align: 'right',
    importance: 1280
  },
  {
    field: 'isActive',
    headerName: 'Attivo',
    type: 'boolean',
    flex: 1,
    editable: false,
    align: 'center',
    importance: 0
  }
];
const rows: GridRowsProp<User> = [];
const title = 'Utenti';
const pageSize = 10;
const actionTitle = 'Crea nuovo';

const DataGridComponent: React.FC = () => {
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [currentUser, setCurrentUser] = useState<User>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const res: Response = await getUsers(); // Chiamata API per ottenere gli utenti
      const getUsersData = await res.json();
      if (res.ok) {
        setRows(getUsersData); // Aggiorna lo stato con i dati ottenuti
        setLoading(false); // Imposta il caricamento a false
      } else {
        alert(getUsersData.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []); // Esegui solo una volta al montaggio del componente

  const refreshUserList = async () => {
    const res: Response = await getUsers();
    const getUsersData = await res.json();
    if (res.ok) {
      setRows(getUsersData); // Aggiorna le righe con i dati più recenti
    } else {
      alert(getUsersData.message);
    }
  };

  useEffect(() => {
    document.title = 'Nanesa - Utenti'; // Set the browser tab title
  }, []);

  // --- Click on a Row ---
  const handleRowClick = (row: User) => {
    setCurrentUser(row as User);
    setIsModalOpen(true);
  };

  const openModal = () => {
    setIsModalOpen(true);
    setCurrentUser(undefined);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Paper elevation={1} sx={{ padding: 2, margin: 2 }}>
        <Box>
          <DataGridCustom
            rows={rows as any}
            columns={columns}
            checkboxSelection={false}
            title={title}
            action={openModal}
            actionTitle={actionTitle}
            hiddenSearchButton={false}
            onRowClick={(params) => handleRowClick(params.row)}
            loading={loading}
          />
        </Box>
      </Paper>
      <BaseModal
        isOpen={isModalOpen}
        onClose={closeModal}
        closeButton={false}
        className={'modal-box'}
      >
        <UserForm twoColumns={true} user={currentUser} refreshUserList={refreshUserList}></UserForm>
        <Box
          sx={{
            mt: 'auto',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Button variant="outlined" color="secondary" onClick={closeModal}>
            Chiudi
          </Button>
        </Box>
      </BaseModal>
    </>
  );
};

export default DataGridComponent;
